// /home/labdoo/zitech-ai/frontend/src/pages/Login/AuthCallback.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import './AuthCallback.css';

interface UserData {
  id: string;
  google_id: string;
  email: string;
  name: string;
  picture: string;
  provider: string;
  total_predictions: number;
  total_correct: number;
  accuracy: number;
  created_at: string;
}

interface AuthResponse {
  status: string;
  user: UserData;
  token: string;
  is_new_user: boolean;
  message: string;
}

// SVG Icons
const ErrorIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <circle cx="12" cy="16" r="0.5" fill="currentColor" stroke="none" />
  </svg>
);

const SpinnerIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="spinner">
    <circle cx="12" cy="12" r="10" strokeDasharray="50" strokeDashoffset="25" />
    <path d="M12 2a10 10 0 1 0 10 10" />
  </svg>
);

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Get the hash fragment from the URL
        const hash = window.location.hash.substring(1);
        const params = new URLSearchParams(hash);
        
        const accessToken = params.get('access_token');
        
        if (!accessToken) {
          throw new Error('No access token received from Google');
        }

        console.log('Access token received, fetching user info...');

        // Fetch user info from Google
        const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        });

        if (!userInfoResponse.ok) {
          throw new Error('Failed to fetch user info from Google');
        }

        const userInfo = await userInfoResponse.json();
        console.log('User info received:', userInfo.email);

        // Send user data to your backend with retry logic
        let backendResponse;
        let retries = 0;
        
        while (retries < MAX_RETRIES) {
          try {
            backendResponse = await api.post<AuthResponse>('/auth/google/', {
              google_id: userInfo.sub,
              email: userInfo.email,
              name: userInfo.name,
              picture: userInfo.picture,
              access_token: accessToken
            });
            
            // If successful, break out of retry loop
            break;
            
          } catch (err: any) {
            retries++;
            console.log(`Backend connection attempt ${retries} failed:`, err.message);
            
            if (retries === MAX_RETRIES) {
              throw new Error('Unable to connect to server. Please try again later.');
            }
            
            // Wait before retrying (exponential backoff)
            await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, retries)));
          }
        }

        if (!backendResponse) {
          throw new Error('Failed to authenticate with server');
        }

        const { user, token, is_new_user } = backendResponse.data;
        
        console.log(`User ${is_new_user ? 'created' : 'logged in'}:`, user.email);

        // Store user info in localStorage - Use 'authToken' consistently
        localStorage.setItem('authToken', token);
        localStorage.setItem('user', JSON.stringify(user));
        
        console.log('✅ Token stored successfully');
        console.log('📝 Token preview:', token.substring(0, 20) + '...');
        
        // REMOVED: The /activities/ endpoint doesn't exist yet
        // We'll skip this to avoid the 404 error

        // Show welcome message for new users
        if (is_new_user) {
          console.log('🎉 Welcome new user!');
        }

        // Small delay to ensure everything is processed
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Redirect to home
        navigate('/');
        
      } catch (err: any) {
        console.error('Auth callback failed:', err);
        setError(err.message || 'Authentication failed. Please try again.');
        
        // Clear any partial data
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
      }
    };

    handleCallback();
  }, [navigate]);

  const handleRetry = () => {
    setError(null);
    setRetryCount(prev => prev + 1);
    // Reload the page to restart the OAuth flow
    window.location.reload();
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  if (error) {
    return (
      <div className="auth-callback-container">
        <div className="auth-error">
          <span className="error-icon"><ErrorIcon /></span>
          <h2>Authentication Failed</h2>
          <p className="error-message">{error}</p>
          
          <div className="error-actions">
            {retryCount < MAX_RETRIES ? (
              <button onClick={handleRetry} className="retry-btn">
                Try Again
              </button>
            ) : (
              <button onClick={handleBackToLogin} className="back-btn">
                Back to Login
              </button>
            )}
          </div>
          
          <details className="error-details">
            <summary>Technical Details</summary>
            <p>If this issue persists, please check:</p>
            <ul>
              <li>Your internet connection</li>
              <li>Backend server is running</li>
              <li>Google OAuth configuration</li>
            </ul>
          </details>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-callback-container">
      <div className="auth-loading">
        <div className="spinner"><SpinnerIcon /></div>
        <p className="loading-text">Completing authentication...</p>
        <p className="loading-subtext">Please wait while we set up your account</p>
      </div>
    </div>
  );
};

export default AuthCallback;